/** XeAgents.ai **/
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertTriangle,
  Calendar,
  CalendarClock,
  Check,
  Clock,
  Filter,
  MoreVertical,
  Phone,
  RefreshCw,
  Users,
  X,
  CalendarDays,
  Trash2,
  CheckCircle2,
} from "lucide-react";
import { isPast } from "date-fns";
import { formatDate, formatRelativeTime } from "@/lib/date-format";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface FollowUpItem {
  journey_id: string;
  contact_id: string;
  campaign_id: string;
  state_machine_id: string;
  current_state: string;
  state_attempt_count: number;
  next_call_at: string;
  next_flow_id: string | null;
  is_complete: boolean;
  persisted_data: Record<string, any>;
  created_at: string;
  updated_at: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_phone: string;
  contact_email: string;
  campaign_name: string;
  state_machine_name: string;
  source_type?: 'journey' | 'followup';
  context_summary?: string;
  followup_reason?: string;
}

interface FollowUpStats {
  total: number;
  overdue: number;
  today: number;
  tomorrow: number;
}

interface FollowUpQueueResponse {
  items: FollowUpItem[];
  stats: FollowUpStats;
}

interface CampaignOption {
  id: string;
  name: string;
}

// State color mapping
const stateColors: Record<string, { bg: string; text: string }> = {
  dial: { bg: "bg-blue-100", text: "text-blue-700" },
  initial_dial: { bg: "bg-blue-100", text: "text-blue-700" },
  gatekeeper: { bg: "bg-orange-100", text: "text-orange-700" },
  gatekeeper_bypass: { bg: "bg-purple-100", text: "text-purple-700" },
  dm_pitch: { bg: "bg-green-100", text: "text-green-700" },
  decision_maker: { bg: "bg-green-100", text: "text-green-700" },
  voicemail: { bg: "bg-sky-100", text: "text-sky-700" },
  voicemail_followup: { bg: "bg-sky-100", text: "text-sky-700" },
  booking: { bg: "bg-emerald-100", text: "text-emerald-700" },
  callback: { bg: "bg-amber-100", text: "text-amber-700" },
  greeting: { bg: "bg-indigo-100", text: "text-indigo-700" },
};

function getStateStyle(state: string) {
  const normalized = state.toLowerCase().replace(/[\s-]/g, "_");
  return stateColors[normalized] || { bg: "bg-gray-100", text: "text-gray-700" };
}

function formatStateName(state: string): string {
  return state
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

export function FollowUpQueueView() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Filters
  const [campaignFilter, setCampaignFilter] = useState<string>("all");
  const [stateFilter, setStateFilter] = useState<string>("all");
  const [timeFilter, setTimeFilter] = useState<string>("all");

  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Dialogs
  const [rescheduleDialog, setRescheduleDialog] = useState<{
    open: boolean;
    journeyId?: string;
    contactName?: string;
    isBulk?: boolean;
  }>({ open: false });
  const [newDateTime, setNewDateTime] = useState("");
  const [rescheduleNotes, setRescheduleNotes] = useState("");

  // Bulk reschedule offset
  const [bulkOffsetDays, setBulkOffsetDays] = useState(0);
  const [bulkOffsetHours, setBulkOffsetHours] = useState(0);

  // Build query params
  const queryParams = new URLSearchParams();
  if (campaignFilter !== "all") queryParams.set("campaignId", campaignFilter);
  if (stateFilter !== "all") queryParams.set("state", stateFilter);
  if (timeFilter !== "all") queryParams.set("timeRange", timeFilter);

  // Fetch follow-up queue
  const { data, isLoading, refetch } = useQuery<FollowUpQueueResponse>({
    queryKey: [`/api/state-machine/follow-up-queue?${queryParams.toString()}`],
  });

  // Fetch filter options
  const { data: campaigns = [] } = useQuery<CampaignOption[]>({
    queryKey: ["/api/state-machine/follow-up-queue/campaigns"],
  });

  const { data: states = [] } = useQuery<string[]>({
    queryKey: ["/api/state-machine/follow-up-queue/states"],
  });

  // Single item actions mutation
  const actionMutation = useMutation({
    mutationFn: async ({
      journeyId,
      action,
      newDateTime,
      notes,
    }: {
      journeyId: string;
      action: "reschedule" | "cancel" | "complete";
      newDateTime?: string;
      notes?: string;
    }) => {
      return apiRequest(`/api/state-machine/follow-up-queue/${journeyId}`, {
        method: "PATCH",
        body: JSON.stringify({ action, newDateTime, notes }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/state-machine/follow-up-queue"] });
      toast({ title: "Success", description: "Follow-up updated successfully" });
      setRescheduleDialog({ open: false });
      setNewDateTime("");
      setRescheduleNotes("");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update follow-up",
        variant: "destructive",
      });
    },
  });

  // Bulk reschedule mutation - apiRequest(method, url, data) handles CSRF
  const bulkRescheduleMutation = useMutation({
    mutationFn: async ({
      journeyIds,
      newDateTime,
      offsetDays,
      offsetHours,
    }: {
      journeyIds: string[];
      newDateTime?: string;
      offsetDays?: number;
      offsetHours?: number;
    }) => {
      const res = await apiRequest("POST", "/api/state-machine/follow-up-queue/bulk-reschedule", {
        journeyIds,
        newDateTime,
        offsetDays,
        offsetHours,
      });
      return res.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/state-machine/follow-up-queue"] });
      toast({
        title: "Success",
        description: `${variables.journeyIds.length} follow-ups rescheduled`,
      });
      setSelectedIds(new Set());
      setRescheduleDialog({ open: false });
      setBulkOffsetDays(0);
      setBulkOffsetHours(0);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to bulk reschedule",
        variant: "destructive",
      });
    },
  });

  const handleSelectAll = () => {
    if (selectedIds.size === (data?.items.length || 0)) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(data?.items.map((i) => i.journey_id) || []));
    }
  };

  const handleSelect = (id: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedIds(newSelection);
  };

  const handleReschedule = (journeyId: string, contactName: string) => {
    setRescheduleDialog({ open: true, journeyId, contactName, isBulk: false });
  };

  const handleBulkReschedule = () => {
    setRescheduleDialog({ open: true, isBulk: true });
  };

  const submitReschedule = () => {
    if (rescheduleDialog.isBulk) {
      if (newDateTime) {
        bulkRescheduleMutation.mutate({
          journeyIds: Array.from(selectedIds),
          newDateTime,
        });
      } else if (bulkOffsetDays > 0 || bulkOffsetHours > 0) {
        bulkRescheduleMutation.mutate({
          journeyIds: Array.from(selectedIds),
          offsetDays: bulkOffsetDays,
          offsetHours: bulkOffsetHours,
        });
      }
    } else if (rescheduleDialog.journeyId && newDateTime) {
      actionMutation.mutate({
        journeyId: rescheduleDialog.journeyId,
        action: "reschedule",
        newDateTime,
        notes: rescheduleNotes,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-96 rounded-lg" />
      </div>
    );
  }

  const stats = data?.stats || { total: 0, overdue: 0, today: 0, tomorrow: 0 };
  const items = data?.items || [];

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-cards">
        <Card className="p-4" data-testid="stat-total-scheduled">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <Phone className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Scheduled</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-red-200 bg-red-50/50" data-testid="stat-overdue">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-100">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-red-600">Overdue</p>
              <p className="text-2xl font-bold text-red-700">{stats.overdue}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4" data-testid="stat-today">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Today</p>
              <p className="text-2xl font-bold">{stats.today}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4" data-testid="stat-tomorrow">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100">
              <CalendarDays className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Tomorrow</p>
              <p className="text-2xl font-bold">{stats.tomorrow}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4" data-testid="filters-section">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filters:</span>
          </div>

          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger className="w-48" data-testid="filter-campaign">
              <SelectValue placeholder="All Campaigns" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Campaigns</SelectItem>
              {campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="w-40" data-testid="filter-state">
              <SelectValue placeholder="All States" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All States</SelectItem>
              {states.map((s) => (
                <SelectItem key={s} value={s}>
                  {formatStateName(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={timeFilter} onValueChange={setTimeFilter}>
            <SelectTrigger className="w-36" data-testid="filter-time">
              <SelectValue placeholder="All Time" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="tomorrow">Tomorrow</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </Card>

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <Card className="p-4 bg-primary/5 border-primary/20">
          <div className="flex items-center justify-between">
            <span className="font-medium">
              {selectedIds.size} follow-up{selectedIds.size > 1 ? "s" : ""} selected
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleBulkReschedule}>
                <CalendarClock className="w-4 h-4 mr-2" />
                Bulk Reschedule
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedIds(new Set())}
              >
                <X className="w-4 h-4 mr-2" />
                Clear Selection
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Follow-up Table */}
      <Card data-testid="followup-table-card">
        <div className="p-4 border-b">
          <h3 className="font-semibold">Scheduled Follow-ups</h3>
          <p className="text-sm text-muted-foreground">
            Manage and reschedule upcoming calls
          </p>
        </div>

        {items.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground" data-testid="empty-state">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No scheduled follow-ups</p>
            <p className="text-sm">Follow-ups will appear here when calls are scheduled</p>
          </div>
        ) : (
          <Table data-testid="followup-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={selectedIds.size === items.length && items.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>Scheduled For</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => {
                const isOverdue = isPast(new Date(item.next_call_at));
                const style = getStateStyle(item.current_state);

                return (
                  <TableRow
                    key={item.journey_id}
                    className={isOverdue ? "bg-red-50/50" : ""}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(item.journey_id)}
                        onCheckedChange={() => handleSelect(item.journey_id)}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {item.contact_first_name} {item.contact_last_name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {item.contact_phone}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.campaign_name}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${style.bg} ${style.text}`}>
                        {formatStateName(item.current_state)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{item.state_attempt_count}</span>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className={`font-medium ${isOverdue ? "text-red-600" : ""}`}>
                          {formatDate(item.next_call_at, "MMM d, h:mm a")}
                        </p>
                        <p className={isOverdue ? "text-red-500" : "text-muted-foreground"}>
                          {isOverdue ? "Overdue - " : ""}
                          {formatRelativeTime(item.next_call_at)}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() =>
                              handleReschedule(
                                item.journey_id,
                                `${item.contact_first_name} ${item.contact_last_name}`
                              )
                            }
                          >
                            <CalendarClock className="w-4 h-4 mr-2" />
                            Reschedule
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              actionMutation.mutate({
                                journeyId: item.journey_id,
                                action: "complete",
                                notes: "Manually marked complete",
                              })
                            }
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Mark Complete
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() =>
                              actionMutation.mutate({
                                journeyId: item.journey_id,
                                action: "cancel",
                                notes: "Manually cancelled",
                              })
                            }
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Cancel Follow-up
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Reschedule Dialog */}
      <Dialog
        open={rescheduleDialog.open}
        onOpenChange={(open) => {
          setRescheduleDialog({ open });
          if (!open) {
            setNewDateTime("");
            setRescheduleNotes("");
            setBulkOffsetDays(0);
            setBulkOffsetHours(0);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {rescheduleDialog.isBulk
                ? `Reschedule ${selectedIds.size} Follow-ups`
                : `Reschedule: ${rescheduleDialog.contactName}`}
            </DialogTitle>
            <DialogDescription>
              {rescheduleDialog.isBulk
                ? "Choose a specific date/time or add an offset to all selected follow-ups."
                : "Select a new date and time for this follow-up call."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Specific Date & Time</Label>
              <Input
                type="datetime-local"
                value={newDateTime}
                onChange={(e) => setNewDateTime(e.target.value)}
              />
            </div>

            {rescheduleDialog.isBulk && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Or add offset
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Days</Label>
                    <Input
                      type="number"
                      min="0"
                      value={bulkOffsetDays}
                      onChange={(e) => setBulkOffsetDays(parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hours</Label>
                    <Input
                      type="number"
                      min="0"
                      value={bulkOffsetHours}
                      onChange={(e) => setBulkOffsetHours(parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>
              </>
            )}

            {!rescheduleDialog.isBulk && (
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Input
                  placeholder="Reason for rescheduling..."
                  value={rescheduleNotes}
                  onChange={(e) => setRescheduleNotes(e.target.value)}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRescheduleDialog({ open: false })}
            >
              Cancel
            </Button>
            <Button
              onClick={submitReschedule}
              disabled={
                (!newDateTime && (!rescheduleDialog.isBulk || (bulkOffsetDays === 0 && bulkOffsetHours === 0))) ||
                actionMutation.isPending ||
                bulkRescheduleMutation.isPending
              }
            >
              {actionMutation.isPending || bulkRescheduleMutation.isPending ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Reschedule
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
